import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { dockIcon, genie, prepareGenie, reducedMotion } from "../utils/genie";

// Genie minimize/restore for a window. Attach `ref` to the window element and add
// `is-genie-hidden` to its classes while `hidden` is true (the real window hides while the
// animated copy plays). `minimize(done)` plays the effect into `app`'s Dock icon, then `done()`
// marks the window minimized. Reopening a minimized window plays it back out of the icon.
// `prepare()` builds the animation ahead of time (call it when a minimize is likely).
const useGenie = ({ app, open, exit }) => {
  const ref = useRef(null);
  const [hidden, setHidden] = useState(false);
  const wasOpen = useRef(open);
  // Only the latest animation may reveal the window when it ends.
  const latest = useRef(0);

  const play = (options, reveal) => {
    const id = ++latest.current;
    setHidden(true);
    genie(options).then((cleanup) => {
      if (latest.current === id) {
        // Show the real window in the same frame the copy goes away.
        if (reveal) flushSync(() => setHidden(false));
        else setHidden(false);
      }
      cleanup();
    });
  };

  const prepare = () => {
    if (ref.current && dockIcon(app)) prepareGenie(ref.current);
  };

  const minimize = (done) => {
    const icon = dockIcon(app);
    if (!ref.current || !icon || reducedMotion()) {
      done();
      return;
    }
    play({ source: ref.current, target: icon }, false);
    done();
  };

  // Runs after the window renders open but before the browser paints, so it never flashes.
  useLayoutEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening || exit !== "minimized") return;
    const icon = dockIcon(app);
    const el = ref.current;
    if (!el || !icon || reducedMotion()) return;
    el.classList.add("is-genie-hidden");
    play({ source: el, target: icon, reverse: true }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exit, app]);

  // While minimized, get the way back out ready when the pointer reaches the Dock icon.
  useEffect(() => {
    if (open || exit !== "minimized") return undefined;
    const onPrepare = (e) => e.detail === app && prepare();
    window.addEventListener("genie-prepare", onPrepare);
    return () => window.removeEventListener("genie-prepare", onPrepare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exit, app]);

  return { ref, hidden, minimize, prepare };
};

export default useGenie;
